import React, { useCallback, useState } from 'react';
import {
  DndContext, DragOverlay, useDraggable, useDroppable, useSensor, useSensors,
  PointerSensor, pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import { useSchoolData } from './SchoolDataProvider';
import { DAY_LABELS, shortDate } from './placement';
import { BLOCK_COLOR } from './blockTheme';
import AssignmentCard from './AssignmentCard';
import NeedsADayStrip, { POOL_ID } from './NeedsADay';

// The week, as the board. Replaces the separate "My Board" tab: the same five
// days the student already reads on This week are now the thing they drop onto,
// so laying out the week and looking at the week are one screen instead of two.
//
// Router-free by construction — the owner's Board tab renders this directly,
// outside any BrowserRouter, to preview exactly what the student sees. Anything
// needing navigation is passed down as an optional callback.
export default function WeekBoard() {
  const {
    weekDates, byDate, today, courseFor, familyId,
    place, unplace, holidayFor, unplacedThisWeek, warningsFor,
  } = useSchoolData();

  const [activeId, setActiveId] = useState(null);
  const [dragWarnings, setDragWarnings] = useState(null);

  // One sensor, not two. The old board registered PointerSensor{distance:5}
  // AND TouchSensor{delay:150}; on touch both activators attach, the 5px
  // threshold is met by any scroll swipe, and dnd-kit then preventDefaults the
  // touchmove — so a scroll gesture starting on a tile turned into a drag.
  // With activation confined to a small grip that can't happen, and a
  // long-press delay on a handle you deliberately grabbed is just friction.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Pointer-first: "the column my finger is over" is what a person means.
  // closestCenter would make every release land somewhere, so a card let go
  // outside the grid would silently jump to the nearest day.
  const collisionDetection = useCallback(args => {
    const hits = pointerWithin(args);
    return hits.length ? hits : rectIntersection(args);
  }, []);

  const activeItem = activeId
    ? [...unplacedThisWeek, ...[...byDate.values()].flat()].find(a => a.id === activeId)
    : null;

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    const dow = Number(over?.id);
    if (!over || !dow) { setDragWarnings(null); return; }
    const item = active.data.current?.item;
    if (!item) { setDragWarnings(null); return; }
    setDragWarnings(warningsFor(item, dow).join(' '));
  }

  function handleDragEnd(event) {
    setActiveId(null);
    setDragWarnings(null);
    const { active, over } = event;
    if (!over) return;
    const item = active.data.current?.item;
    if (!item) return;
    if (over.id === POOL_ID) { unplace(item); return; }
    const dow = Number(over.id);
    if (dow) place(item, dow);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => { setActiveId(null); setDragWarnings(null); }}
    >
      <NeedsADayStrip />

      {dragWarnings && <p style={{ color: 'var(--bad)', fontSize: 13, margin: '0 0 8px' }}>⚠️ {dragWarnings}</p>}

      <div className="db-eyebrow" style={{ marginBottom: 6 }}>This week</div>
      <div className="school-week-board">
        {[1, 2, 3, 4, 5].map((dow, i) => (
          <DayColumn
            key={dow}
            dow={dow}
            date={weekDates[i]}
            items={byDate.get(weekDates[i]) || []}
            holiday={holidayFor(weekDates[i])}
            isToday={weekDates[i] === today}
            courseFor={courseFor}
            familyId={familyId}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="db-card" style={{ padding: '8px 12px', borderLeft: `3px solid ${BLOCK_COLOR[activeItem.block] || 'var(--ink-3)'}`, cursor: 'grabbing' }}>
            <div className="db-item-title">{activeItem.title}</div>
            <div className="db-item-sub">{activeItem.estMinutes}m</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function DayColumn({ dow, date, items, holiday, isToday, courseFor, familyId }) {
  const { setNodeRef, isOver } = useDroppable({ id: String(dow), disabled: Boolean(holiday) });
  const minutes = items.reduce((s, a) => s + (a.estMinutes || 0), 0);

  if (holiday) {
    return (
      <div className="db-card db-day" style={{ padding: 10, opacity: 0.6 }}>
        <div className="db-eyebrow">{DAY_LABELS[dow]} · {shortDate(date)}</div>
        <p className="db-item-sub" style={{ margin: 0 }}>{holiday} — no school</p>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`db-card db-day db-drop ${isOver ? 'db-drop--over' : ''} ${isToday ? 'db-day--today' : ''}`}
      style={{ minHeight: items.length ? undefined : 44 }}
    >
      <div className="db-eyebrow" style={{ marginBottom: items.length ? 6 : 0 }}>
        {DAY_LABELS[dow]} · {shortDate(date)}{isToday && ' · Today'}
      </div>
      {items.length === 0 ? (
        <p className="db-item-sub" style={{ margin: 0 }}>Nothing yet</p>
      ) : (
        <>
          <div className="db-item-sub" style={{ marginBottom: 4 }}>
            {items.length} item{items.length === 1 ? '' : 's'} · {minutes}m
          </div>
          <ul className="db-items" style={{ padding: 0 }}>
            {items.map(a => (
              a.placementMode === 'student-placed'
                ? <DraggableRow key={a.id} a={a} course={courseFor(a)} familyId={familyId} />
                : <AssignmentCard key={a.id} a={a} course={courseFor(a)} familyId={familyId} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// A placed tile: the whole card measures and moves, but only the grip starts a
// drag (setActivatorNodeRef). The card stays put at reduced opacity while a
// DragOverlay ghost follows the pointer — a transform on the row itself would
// be clipped by .db-block's `overflow: hidden` and would keep its narrow
// column's width while being dragged across the page.
function DraggableRow({ a, course, familyId }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: a.id, data: { item: a } });

  return (
    <AssignmentCard
      a={a}
      course={course}
      familyId={familyId}
      itemRef={setNodeRef}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      leading={
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="db-grip"
          aria-label={`Move ${a.title} to a different day`}
          {...listeners}
          {...attributes}
        >
          ⠿
        </button>
      }
    />
  );
}
